import '../input/button-input';

import { html, LitElement, TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { getScrollParent } from '../../_utils/scroll-parent';

@customElement('side-scroller')
export class SideScroller extends LitElement {

    // Props

	@property({ type: Number, attribute: 'entry-count' })
	entryCount: number = 0;

	@property({ type: Boolean })
	infinite: boolean = false;

	@property({ type: String, attribute: 'header-url' })
	headerUrl: string|null = null;

	@property({ type: String, attribute: 'header-title' })
	headerTitle?: string = '';

	@query('.SideScroller-container')
	_scrollContainer!: HTMLElement;

	@query('.SideScroller-container > *')
	_scrollElement!: HTMLElement;

	@query('.SideScroller-guard')
	_scrollGuard!: HTMLElement;

	@state()
	_columnCount: number = 4; // set via CSS var

	@state()
    _header: string = '';

	@state()
    _body: string = '';

	@state()
	_busy: boolean = false;

	@state()
	_navIsVisible: boolean = false;

	@state()
	_scrollTime: number = 0;

    // Lifecycle Hooks

    firstUpdated() {

        // class list
        this.classList.add('SideScroller');

        // body slot
        const bodySlot = this.querySelector('[slot="body"]');
		if (bodySlot) {
			this._body = bodySlot.innerHTML;
        	bodySlot.remove();
		}
        
		// header slot
		const headerSlot = this.querySelector('[slot="header"]');
		if (headerSlot) {
			this._header = headerSlot.innerHTML;
        	headerSlot.remove();
		}

		if (this.infinite) {

			// set initial position
			requestAnimationFrame(() => {
				this.setInitialPosition();
			});

			// wheel listener
			this._scrollContainer.addEventListener('wheel', (e: WheelEvent) => {
				e.preventDefault();
				if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
					this.handleWheel(e.deltaX);
				} else {
					const scrollParent = getScrollParent(this._scrollContainer);
					if (scrollParent) scrollParent.scrollTop += e.deltaY;
				}
			}, { capture: true, passive: false });

			// touch listener
			this._scrollContainer.addEventListener('touchstart', (e: TouchEvent) => {
				e.preventDefault();
				const startTouch = [e.touches[0].clientX, e.touches[0].clientY];
				let lastTouch = [...startTouch];

				const handleMove = (e: TouchEvent) => {
					const currentTouch = [e.touches[0].clientX, e.touches[0].clientY];
					const deltaX = lastTouch[0] - currentTouch[0];
					const deltaY = lastTouch[1] - currentTouch[1];
					lastTouch = [...currentTouch];
					if (Math.abs(deltaX) > Math.abs(deltaY)) {
						this.handleWheel(deltaX);
					} else {
						const scrollParent = getScrollParent(this._scrollContainer);
						if (scrollParent) scrollParent.scrollTop += deltaY;
					}
				}
				
				const handleEnd = () => {
					this._scrollContainer.removeEventListener('touchmove', handleMove);
				}

				this._scrollContainer.addEventListener('touchmove', handleMove);
				this._scrollContainer.addEventListener('touchend', handleEnd);

			}, { capture: true, passive: false });
			
		}
		
		// show/hide nav, set padding attribute
		setTimeout(() => {
			this.handleResize();
			window.addEventListener('resize', this.handleResize.bind(this));
		}, 0);
    }

    // Render

    render() {
        return html`
			<header class="SideScroller-header">
				${ this.headerTitle ? this.headerTemplate() : '' }
				${ this._navIsVisible ? this.navTemplate() : '' }
			</header>
			<div class="SideScroller-container">
            	${ unsafeHTML(this._body) }
			</div>
        `;
    }

    createRenderRoot() { 
        return this;
    }

	// Templates

	navTemplate(): TemplateResult {
		return html`
			<nav class="SideScroller-nav">
				<div class="SideScroller-navItem SideScroller-navItem--prev">
					<button-input
						name="scroller"
						.disabled="${ this._busy }"
						@click="${ this.moveBack }">
						<div slot="label">
							←
						</div>
					</button-input>
				</div>
				<div class="SideScroller-navItem SideScroller-navItem--next">
					<button-input
						name="scroller"
						.disabled="${ this._busy }"
						@click="${ this.moveForwards }">
						<div slot="label">
							→
						</div>
					</button-input>
				</div>
			</nav>
		`;
	}

	headerTemplate(): TemplateResult {
		return html`
			${ this.headerUrl ? this.buttonHeader() : this.textHeader() }
		`;
	}

	buttonHeader(): TemplateResult {
		return html`
			<h3 class="SideScroller-title SideScroller-title--button">
				<button-input
					type="link"
					name="nav"
					href="${ this.headerUrl ?? '#' }">
					<div slot="label">
						${ this.headerTitle }
					</div>
				</button-input>
			</h3>
		`;
	}

	textHeader(): TemplateResult {
		return html`
			<h3 class="SideScroller-title SideScroller-title--text">
				${ this.headerTitle }
			</h3>
		`;
	}

	// Methods

	setInitialPosition() {
		this._scrollContainer.scrollLeft = this.thumbnailWidth /2;
		this._scrollContainer.scrollTo({ 
			left: this.thumbnailWidth / 2 ,
			behavior: 'instant' 
		});
	}

	handleWheel(delta: number) {
		if (!this._busy) {
			this._busy = true;
			if (delta < 0) {				
				this.moveBack();
			} else if (delta > 0) {
				this.moveForwards();
			}
			setTimeout(() => {
				this._busy = false;
			}, 1000);
		}
	}

	handleResize() {
		this.toggleNav();
		this.style.setProperty('--padding-left', this.paddingLeft);
		this.style.setProperty('--padding-right', this.paddingRight);
		this.style.setProperty('--gap', this.gap);
		this.style.setProperty('--container-width', `${this._scrollContainer.clientWidth}px`);
		this.classList.toggle('is-static', this.entryCount < this.columnCount);
	}

	// Methods

	moveBack() {
		if (this.infinite) {
			const scrollX = this._scrollContainer.scrollLeft;
			if (scrollX <= this.thumbnailWidth) {
				this._scrollElement.insertBefore(
					this._scrollElement.lastElementChild!, 
					this._scrollElement.firstElementChild
				);
				this._scrollContainer.scrollTo({ 
					left: scrollX + this.thumbnailWidth, 
					behavior: 'instant' 
				});
			}
		}
		this._scrollContainer.scrollTo({ 
			left: this._scrollContainer.scrollLeft - this.thumbnailWidth, 
			behavior: 'smooth' 
		});
	}

	moveForwards() {
		if (this.infinite) {
			const scrollX = this._scrollContainer.scrollLeft;
			if (scrollX >= this.width - this.thumbnailWidth) {
				this._scrollElement.appendChild(
					this._scrollElement.firstElementChild!
				);
				this._scrollContainer.scrollTo({ 
					left: scrollX - this.thumbnailWidth, 
					behavior: 'instant' 
				});
			}
		}
		this._scrollContainer.scrollTo({ 
			left: this._scrollContainer.scrollLeft + this.thumbnailWidth, 
			behavior: 'smooth' 
		});
	}

	toggleNav() {
		if (this.entryCount < this.columnCount) {
			this._navIsVisible = false;
		} else {
			this._navIsVisible = true;
		}
	}

	// Getters

	get width() {
		const bbox = this.getBoundingClientRect()
		return bbox.right - bbox.left;
	}

	get thumbnailWidth() {
		return this.width/this.columnCount;
	}

	get columnCount() {
		const styleDef = Number(getComputedStyle(this._scrollContainer.firstElementChild!).getPropertyValue('--column-count'))
			|| Number(getComputedStyle(this).getPropertyValue('--column-count'));
		const columnCount = (styleDef) ? styleDef : String(this._columnCount);
		return Number(columnCount);
	}

	get paddingLeft() {
		return getComputedStyle(this._scrollContainer.firstElementChild!).getPropertyValue('padding-left');
	}

	get paddingRight() {
		return getComputedStyle(this._scrollContainer.firstElementChild!).getPropertyValue('padding-right');
	}

	get gap() {
		return getComputedStyle(this._scrollContainer.firstElementChild!).getPropertyValue('gap');
	}

}